import { useRef, FC, ReactNode, useEffect, useState, useMemo } from 'react';
import { AccordionHandler } from './handler';
import { KeyHandler } from './KeyHandler';
import { shallowEqual } from '@apia/store';
import { uniqueId } from '../ListBox/common';

export interface IAccordionProps {
  /**
   * Tiempo en ms que dura la animación. Por defecto se usa 150.
   */
  animationDuration?: number;
  children?: ReactNode;
  className?: string;
  getHandler?: (handler: AccordionHandler) => unknown;
  singleExpand?: boolean;
  /**
   * Por defecto usa la variante layout.common.components.accordion.primary
   */
  variant?: string;
}

export const Accordion: FC<IAccordionProps> = ({
  animationDuration = 150,
  children,
  className,
  getHandler,
  variant = 'layout.common.components.accordion.primary',
  singleExpand = false,
}) => {
  const id = useMemo(() => uniqueId(), []);

  const [handler] = useState(() => {
    return new AccordionHandler(id, {
      animationDuration,
      children,
      getHandler,
      variant,
      singleExpand,
    });
  });
  const propsRef = useRef({
    animationDuration,
    children,
    getHandler,
    variant,
  });
  if (
    !shallowEqual(
      {
        animationDuration,
        children,
        getHandler,
        variant,
      },
      propsRef.current,
    )
  ) {
    propsRef.current = {
      animationDuration,
      children,
      getHandler,
      variant,
    };
    handler.updateProps({
      animationDuration,
      children,
      getHandler,
      variant,
    });
  }

  const Provider = handler.hooks.useAccordionContextProvider();

  useEffect(() => {
    getHandler?.(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getHandler, handler]);

  return (
    <Provider>
      <KeyHandler className={`accordion ${className ?? ''}`}>
        {children}
      </KeyHandler>
    </Provider>
  );
};
